
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AgentExecutionResponse } from "@/models/agent-runtime";
import { ErrorDisplay } from "./results/ErrorDisplay";
import { ResultsHeader } from "./results/ResultsHeader";
import { ResultContent } from "./results/ResultContent";

interface AgentRuntimeResultsProps {
  result: AgentExecutionResponse | null;
}

export function AgentRuntimeResults({ result }: AgentRuntimeResultsProps) {
  if (!result) {
    return (
      <Card className="w-full h-full min-h-[300px] flex items-center justify-center">
        <p className="text-muted-foreground">
          Execute an agent to see results here
        </p>
      </Card>
    );
  }

  if (!result.success) {
    return (
      <ErrorDisplay 
        error={result.error || "Unknown error"} 
        logs={result.logs} 
      />
    );
  }

  // Determine result type
  const isJson = typeof result.output === "object";
  const isMarkdown = typeof result.output === "string" && 
    (result.output.includes("#") || result.output.includes("```") || result.output.includes("*"));
  
  return (
    <Card className="w-full h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xl">Result</CardTitle>
        <ResultsHeader 
          executionTime={result.executionTime}
          tokenUsage={result.tokenUsage}
        />
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="result">
          <TabsList>
            <TabsTrigger value="result">Result</TabsTrigger>
            {isJson && <TabsTrigger value="json">JSON</TabsTrigger>}
            {result.logs && <TabsTrigger value="logs">Logs</TabsTrigger>}
          </TabsList>
          
          <TabsContent value="result" className="py-4">
            <ResultContent 
              output={result.output}
              isJson={isJson}
              isMarkdown={isMarkdown}
            />
          </TabsContent>
          
          {isJson && (
            <TabsContent value="json" className="py-4">
              <ScrollArea className="h-[400px]">
                <pre className="p-4 rounded-md bg-gray-50 dark:bg-gray-900 text-sm font-mono">
                  {JSON.stringify(result.output, null, 2)}
                </pre>
              </ScrollArea>
            </TabsContent>
          )}
          
          {result.logs && (
            <TabsContent value="logs" className="py-4">
              <ScrollArea className="h-[400px]">
                <div className="p-4 rounded-md bg-gray-50 dark:bg-gray-900 font-mono text-xs">
                  {result.logs.map((log, index) => (
                    <div key={index} className="pb-1">{log}</div>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
}
